<!--
  SegmentedSelect.vue

  Segmented single-select for small, bounded VALUE fields — the pill-shaped
  sibling of ValueSelect (native <select>). One self-aware control for the
  deal_status and visibility forms: it derives its options from the field and
  looks up each value's glyph/colour from the shared maps below — no per-caller
  slots.

  Only for enumerated value types (visibility, *_status). Reference fields are
  unbounded and stay on ReferenceSelect / ReferenceInput.

  `mode` picks what each segment shows:
    icon   glyph only (values without a glyph fall back to their label)
    text   capitalised label only
    both   glyph + label, collapsing to glyph-only when the container is narrow
-->
<template>
  <div
    class="segmented-select"
    :class="[sizeClass, `is-${size}`, `is-${effectiveMode}`]"
    role="radiogroup"
  >
    <button
      v-for="option in options"
      :key="option.value"
      type="button"
      role="radio"
      class="segment"
      :class="{ 'is-active': String(option.value) === current }"
      :style="
        String(option.value) === current ? { '--seg-active': colorFor(option.value) } : undefined
      "
      :aria-checked="String(option.value) === current"
      :aria-label="option.label"
      :title="option.label"
      :disabled="readonly"
      @click="select(option.value)"
    >
      <font-awesome-icon
        v-if="effectiveMode !== 'text' && iconFor(option.value)"
        class="seg-icon"
        :icon="iconFor(option.value)"
      />
      <span v-if="effectiveMode !== 'icon'" class="seg-label">{{ option.label }}</span>
    </button>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue"
import { type Field, getFieldType, selectOptions } from "@auger/core/fields"
import { type FieldSize, textSizeClass } from "../useField"

// Field renderer driven by modelValue + field; keep the form host's extra props
// (item, placeholder, `changed` listener) off the root.
defineOptions({ inheritAttrs: false })

const props = withDefaults(
  defineProps<{
    modelValue?: string | number | null
    field?: Field
    readonly?: boolean
    size?: FieldSize
    mode?: "icon" | "text" | "both"
  }>(),
  { modelValue: null, field: undefined, readonly: false, size: "normal", mode: undefined }
)

const emit = defineEmits<{ "update:modelValue": [value: string | number] }>()

const sizeClass = computed(() => textSizeClass(props.size))
const current = computed(() => (props.modelValue == null ? "" : String(props.modelValue)))

// Options from the field's own `values`, falling back to the registered type's
// baked values (schema fields carry them; built-in columns may not). Labels are
// title-cased by selectOptions (`todo` → `Todo`).
const options = computed(() => {
  const typeDef = props.field ? getFieldType(props.field.type) : undefined
  const typeValues = (typeDef as { values?: readonly unknown[] } | undefined)?.values
  return selectOptions({ values: props.field?.values ?? typeValues })
})

// Per-value UI decoration lives here with the control, not on the field type:
// the type owns the option *values*, this owns how they look. Fine if a glyph
// is duplicated by the visibility tags/cells — they're a separate surface.
// value → glyph (statuses carry none and render as text).
const ICONS: Record<string, string> = {
  private: "user",
  shared: "user-friends",
  public: "users",
}

// value → active-fill colour. Statuses use the semantic tokens (mirroring the
// StatusIcon convention: neutral / info / success / danger); visibility keeps
// deliberate, specific shades since it's a scope, not a semantic state.
const COLORS: Record<string, string> = {
  todo: "var(--auger-text-weak)",
  doing: "var(--auger-info)",
  done: "var(--auger-success)",
  dismissed: "var(--auger-text-weak)",
  draft: "var(--auger-text-weak)",
  submitted: "var(--auger-info)",
  approved: "var(--auger-success)",
  rejected: "var(--auger-danger)",
  open: "var(--auger-info)",
  won: "var(--auger-success)",
  lost: "var(--auger-danger)",
  private: "rgb(146, 171, 197)",
  shared: "slategray",
  public: "rgb(74, 85, 96)",
}

const iconFor = (value: string | number) => ICONS[String(value)]
const colorFor = (value: string | number) => COLORS[String(value)] ?? "var(--auger-primary)"

// Default the display off the options themselves — glyph-only when every value
// has one (visibility), text otherwise (statuses) — so the registry needs no
// per-type config. An explicit `mode` (incl. "both") overrides.
const effectiveMode = computed<"icon" | "text" | "both">(
  () =>
    props.mode ??
    (options.value.length > 0 && options.value.every((o) => iconFor(o.value)) ? "icon" : "text")
)

function select(value: string | number) {
  if (props.readonly) return
  emit("update:modelValue", value)
}
</script>

<style>
.segmented-select {
  display: inline-flex;
  width: 100%;
  /* Query against the field column width so `both` can drop labels when narrow. */
  container-type: inline-size;

  .segment {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 0.4em;
    padding: 0.4em 0.75em;
    border: 1px solid var(--auger-border);
    background: var(--auger-bg);
    color: var(--auger-text-weak);
    font: inherit;
    line-height: 1;
    white-space: nowrap;
    cursor: pointer;

    &:not(:last-child) {
      border-right: none;
    }
    &:first-child {
      border-top-left-radius: var(--auger-radius);
      border-bottom-left-radius: var(--auger-radius);
    }
    &:last-child {
      border-top-right-radius: var(--auger-radius);
      border-bottom-right-radius: var(--auger-radius);
    }

    &:hover:not(.is-active):not(:disabled) {
      color: var(--auger-text);
    }

    /* Active fill comes from the per-value `--seg-active` set inline. */
    &.is-active {
      background: var(--seg-active);
      border-color: transparent;
      color: white;
    }

    &:disabled {
      cursor: default;
    }
  }

  /* Icon-only: tighter, squarer buttons. */
  &.is-icon .segment {
    padding: 0.4em 0.6em;
  }

  /* Small stands at the shared small-control height. Padding sets the height at
     every other size; here it's stated, so the vertical half of that padding
     would only push past it. */
  &.is-small .segment {
    height: var(--auger-control-height-small);
    padding-block: 0;
  }
}

/* Narrow column → drop the label where a glyph can stand in for it. */
@container (max-width: 240px) {
  .segmented-select .segment:has(.seg-icon) .seg-label {
    display: none;
  }
}
</style>
